import { Fragment, useId, useState } from 'react';
import { NJButton, NJHeading, NJIconButton, NJPopover, NJText } from '@engie-group/fluid-design-system-react';
import { UMIcon } from '../../urbanmap/_components';
import { PlanLogo } from './ui';
import { UMR } from './tokens';

const FOOTER_ITEMS = [
  { icon: 'settings', label: 'Admin', message: 'Administration · à venir dans ce prototype' },
  { icon: 'mail', label: 'Contact', message: 'Contact · à venir dans ce prototype' },
  { icon: 'logout', label: 'Déconnexion', message: 'Déconnexion non disponible · vous êtes dans un prototype' },
] as const;

const PROJECT_ITEMS = [
  { icon: 'layers', label: 'Couches' },
  { icon: 'filter_list', label: 'Filtres' },
  { icon: 'folder_open', label: 'Sélection' },
  { icon: 'route', label: 'Plans de développement' },
  { icon: 'file_download', label: 'Export' },
  { icon: 'file_upload', label: 'Import' },
] as const;

const DIVIDER_AFTER = new Set<string>(['layers', 'folder_open', 'route']);

function PlanAppSwitcher({ disabled, onHome, isHome }: { disabled: boolean; onHome: () => void; isHome: boolean }) {
  const [open, setOpen] = useState(false);
  const titleId = useId();
  return (
    <NJPopover.Root opened={open && !disabled} onOpen={setOpen}
      useFloatingOptions={{ placement: 'right-start', strategy: 'fixed' }}>
      <NJPopover.Interactions dismiss role={{ role: 'dialog' }}>
        <NJPopover.Anchor>
          <button type="button" className="pcv3-home-brand pcv3-brand-trigger"
            aria-label="Plan · accueil et changement d’application" aria-haspopup="dialog" aria-expanded={open && !disabled}
            disabled={disabled} onClick={() => setOpen((value) => !value)}>
            <PlanLogo size={UMR.size.logo + 8} />
          </button>
        </NJPopover.Anchor>
        <NJPopover.Content focusManagerProps={{ modal: false }}>
          <div className="pcv3-app-switcher" aria-labelledby={titleId}>
            <NJButton variant="secondary" emphasis="subtle" icon="home" label="Accueil Plan" className="pcv3-app-switcher-home"
              aria-current={isHome ? 'page' : undefined} onClick={() => { setOpen(false); onHome(); }} />
            <hr className="pcv3-app-switcher-divider" />
            <div className="pcv3-app-switcher-heading">
              <NJHeading as="h2" scale="xs" id={titleId}>Changer d’application</NJHeading>
              <NJIconButton icon="close" label="Fermer le sélecteur d’application" variant="tertiary" scale="sm"
                onClick={() => setOpen(false)} />
            </div>
            <NJText scale="sm">Application actuelle : Plan</NJText>
            <NJButton href="/urbanmap" variant="secondary" icon="map" label="UrbanMap" />
            <NJButton href="/size2mix" variant="secondary" icon="hub" label="Size2Mix" />
          </div>
        </NJPopover.Content>
      </NJPopover.Interactions>
    </NJPopover.Root>
  );
}

export function HomeRail({ onHome, flash, isHome = true, disabled = false, projectNavigation = false, onSettings, settingsOpen = false, onPlans, plansOpen = false, onFilters, filtersOpen = false, onSelection, selectionOpen = false, onLayers, layersOpen = false }: {
  onHome: () => void;
  flash: (message: string) => void;
  isHome?: boolean;
  disabled?: boolean;
  projectNavigation?: boolean;
  onSettings?: () => void;
  settingsOpen?: boolean;
  onPlans?: () => void;
  plansOpen?: boolean;
  onFilters?: () => void;
  filtersOpen?: boolean;
  onSelection?: () => void;
  selectionOpen?: boolean;
  onLayers?: () => void;
  layersOpen?: boolean;
}) {
  const panels: Partial<Record<string, { open: boolean; toggle?: () => void }>> = {
    layers: { open: layersOpen, toggle: onLayers },
    route: { open: plansOpen, toggle: onPlans },
    filter_list: { open: filtersOpen, toggle: onFilters },
    folder_open: { open: selectionOpen, toggle: onSelection },
  };
  return (
    <nav className={`pcv3-home-rail${projectNavigation ? ' pcv3-project-rail' : ''}`} aria-label="Navigation Plan">
      <PlanAppSwitcher disabled={disabled} onHome={onHome} isHome={isHome} />
      {projectNavigation && onSettings && (
        <>
          <button type="button" className="pcv3-home-rail-action" aria-pressed={settingsOpen}
            aria-label="Configuration projet" onClick={onSettings}>
            <UMIcon name="settings" size={UMR.size.railIcon} color={settingsOpen ? 'currentColor' : UMR.text} />
            <NJText as="span" scale="xs">Configuration projet</NJText>
          </button>
          <button type="button" className="pcv3-home-rail-action" aria-label="Hypothèses algorithmes"
            onClick={() => flash('Hypothèses algorithmes · à venir dans ce prototype')}>
            <UMIcon name="tune" size={UMR.size.railIcon} color={UMR.text} />
            <NJText as="span" scale="xs">Hypothèses algorithmes</NJText>
          </button>
          <hr className="pcv3-home-rail-divider" />
        </>
      )}
      {projectNavigation && PROJECT_ITEMS.map((item) => {
        const target = panels[item.icon];
        const toggle = target?.toggle;
        return (
          <Fragment key={item.icon}>
            <button type="button" className="pcv3-home-rail-action"
              aria-pressed={toggle ? target.open : undefined}
              onClick={toggle ?? (() => flash(`${item.label} · à venir dans ce prototype`))}>
              <UMIcon name={item.icon} size={UMR.size.railIcon} color={target?.open ? 'currentColor' : UMR.text} />
              <NJText as="span" scale="xs">{item.label}</NJText>
            </button>
            {DIVIDER_AFTER.has(item.icon) && <hr className="pcv3-home-rail-divider" />}
          </Fragment>
        );
      })}
      <div className="pcv3-home-rail-footer">
        {FOOTER_ITEMS.map((item) => (
          <button key={item.icon} type="button" className="pcv3-home-rail-action" onClick={() => flash(item.message)}>
            <UMIcon name={item.icon} size={UMR.size.railIcon} color={UMR.text} />
            <NJText as="span" scale="xs">{item.label}</NJText>
          </button>
        ))}
      </div>
    </nav>
  );
}
